/**
 * 秒杀商品价格（与后台「设置商品」表格一致）
 *
 * 每一档规格行语义：
 * - 秒杀价：后台「活动价格」列（接口字段 activity_price / recover 后写在 price 等）
 * - 划线价：同一行的「价格」列——非活动时的售卖价（日常价）；recover 时写在 old_price
 *
 * 首页卡片：主价 = 各档秒杀价中的最小值；划线价 = 与该最小秒杀价同一档对应的日常价。
 * 禁止把「仅因存在 item_goods_sku_price」的档位 price 当成秒杀价（会得到 min(2325,2075,…) 错误）。
 */

function parseMoney(v) {
  if (v == null || v === '') return NaN;
  return parseFloat(String(v).replace(/,/g, ''));
}

/** activity_price 等有时为 { price: "100" }，直接 return 会导致 parseMoney 为 NaN */
function coerceScalarPrice(v) {
  if (v == null || v === '') return null;
  if (Array.isArray(v) && v.length) return coerceScalarPrice(v[0]);
  if (typeof v === 'object') {
    const inner =
      v.price ?? v.activity_price ?? v.activity_goods_price ?? v.value ?? v.amount;
    if (inner == null || inner === '') return null;
    if (typeof inner === 'object') return coerceScalarPrice(inner);
    return inner;
  }
  return v;
}

/** 嵌套对象里找活动价（如 tenant_mall_activity_goods_sku_price: { activity_price: "100" }） */
function deepPickActivityPriceScalar(row, depth) {
  const d = depth == null ? 0 : depth;
  if (!row || typeof row !== 'object' || d > 3) return null;
  const skip = new Set([
    'item_goods_sku_price',
    'goods_sku_price',
    'skus',
    'children',
    'images',
  ]);
  for (const [k, v] of Object.entries(row)) {
    if (skip.has(k)) continue;
    if (v == null || v === '') continue;
    const keyLow = k.toLowerCase();
    if (
      typeof v === 'object' &&
      !Array.isArray(v) &&
      /activity|seckill|mall_|tenant_|promotion|flash|rush|特价/i.test(k)
    ) {
      const inner = deepPickActivityPriceScalar(v, d + 1);
      if (inner != null) return inner;
    }
    if (typeof v === 'object' && !Array.isArray(v)) continue;
    const raw = coerceScalarPrice(v);
    if (raw == null || raw === '') continue;
    const n = parseMoney(raw);
    if (Number.isNaN(n) || n < 0.01) continue;
    if (
      /activity|seckill|sale|flash|promotion|event|特价|秒杀/i.test(k) &&
      /price|amount|fee/i.test(k)
    ) {
      return raw;
    }
  }
  return null;
}

/** 接口常在根给 activity_sku_prices，也可能只在 data.activity.activity_sku_prices */
export function pickActivitySkuPricesExtra(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const a = payload.activity_sku_prices;
  if (Array.isArray(a) && a.length) return a;
  const act = payload.activity;
  if (act && Array.isArray(act.activity_sku_prices) && act.activity_sku_prices.length) {
    return act.activity_sku_prices;
  }
  return null;
}

/** 将 activity_sku_prices 与 sku_prices 按下标合并（列表合详情均可能只给并行数组） */
function effectiveSkuRowsForPricing(goods) {
  const main =
    goods.new_sku_prices ||
    goods.sku_prices ||
    null;
  if (!Array.isArray(main) || !main.length) return [];
  const extra = pickActivitySkuPricesExtra(goods);
  if (!Array.isArray(extra) || !extra.length) return main;
  return main.map((row, i) => {
    if (!row || typeof row !== 'object') return row;
    const e = extra[i];
    if (!e || typeof e !== 'object') return row;
    return { ...row, ...e };
  });
}

function formatMoneyText(n) {
  const x = parseFloat(String(n));
  if (Number.isNaN(x)) return '';
  const s = x.toFixed(2);
  return s.endsWith('.00') ? s.slice(0, -3) : s;
}

/** 详情 / 卡片展示用数字格式化 */
export function formatActivityMoneyDisplay(n) {
  return formatMoneyText(n);
}

/**
 * 接口里 min_price 常表示「活动价/秒杀价」，也可能误传起订量；仅当明显低于同档日常价才采纳。
 * （不再用「catalog>1500 且 mn<50」否决——会误伤百元档真实秒杀；catalog 已由 parseSkuRowListPriceRaw 纠正）
 */
function minPriceLooksLikeActivitySale(mn, catalog) {
  if (Number.isNaN(mn) || mn < 0.01) return false;
  if (Number.isNaN(catalog)) return mn < 1e7;
  if (mn >= catalog - 0.01) return false;
  return mn <= catalog * 0.92 || catalog - mn > 25;
}

function minPriceAsActivityRaw(row) {
  if (!row || row.min_price == null || String(row.min_price).trim() === '') return null;
  const mn = parseMoney(row.min_price);
  const catalog = parseSkuRowListPriceRaw(row);
  if (minPriceLooksLikeActivitySale(mn, catalog)) return row.min_price;
  return null;
}

/** 兼容后端非常规字段名：键名像活动价且数值明显低于日常价 */
function scavengeActivitySaleRaw(row) {
  if (!row || typeof row !== 'object') return null;
  const catalog = parseSkuRowListPriceRaw(row);
  const keyRe =
    /(activity|seckill|flash|promotion|rush|特价).*(price|amount|fee)|^event_price$|^promotion_price$/i;
  let best = null;
  let bestN = Infinity;
  for (const [k, v] of Object.entries(row)) {
    if (v == null || v === '' || typeof v === 'object') continue;
    if (!keyRe.test(k)) continue;
    if (/id|status|type|stock|num|count|time$/i.test(k)) continue;
    const n = parseMoney(v);
    if (Number.isNaN(n) || n < 1) continue;
    if (!Number.isNaN(catalog) && n >= catalog - 0.01) continue;
    if (!Number.isNaN(catalog) && !(n <= catalog * 0.92 || catalog - n > 25)) continue;
    if (n < bestN) {
      bestN = n;
      best = v;
    }
  }
  return best;
}

/** 从规格行读取明确的活动价字段（不含 recover、不含日常价 price） */
function parseExplicitActivityRaw(row) {
  if (!row || typeof row !== 'object') return null;
  const keys = [
    'activity_price',
    'activityPrice',
    'activity_goods_price',
    'activity_sale_price',
    'activitySalePrice',
    'seckill_price',
    'seckillPrice',
    'spec_activity_price',
    'event_price',
    'promotion_price',
    'mall_activity_price',
    'sku_activity_price',
    'activity_sku_price',
  ];
  for (let i = 0; i < keys.length; i++) {
    const raw = coerceScalarPrice(row[keys[i]]);
    if (raw != null && raw !== '') return raw;
  }
  if (row.activity && row.activity.price != null && row.activity.price !== '') {
    const ar = coerceScalarPrice(row.activity.price);
    if (ar != null && ar !== '') return ar;
  }
  const nestedObjs = [
    row.activity_sku,
    row.activity_sku_price,
    row.tenant_activity_sku_price,
    row.mall_activity_sku_price,
  ];
  for (let n = 0; n < nestedObjs.length; n++) {
    const sub = nestedObjs[n];
    if (!sub || typeof sub !== 'object') continue;
    const nk = [
      'activity_price',
      'price',
      'sale_price',
      'activity_sale_price',
    ];
    for (let k = 0; k < nk.length; k++) {
      const raw = coerceScalarPrice(sub[nk[k]]);
      if (raw != null && raw !== '') return raw;
    }
  }
  if (row.item_goods_sku_price && typeof row.item_goods_sku_price === 'object') {
    const ig = row.item_goods_sku_price;
    for (let i = 0; i < keys.length; i++) {
      const raw = coerceScalarPrice(ig[keys[i]]);
      if (raw != null && raw !== '') return raw;
    }
    if (ig.min_price != null && String(ig.min_price).trim() !== '') {
      const mn = parseMoney(ig.min_price);
      const cat = parseSkuRowListPriceRaw(row);
      if (minPriceLooksLikeActivitySale(mn, cat)) return ig.min_price;
    }
  }
  const mp = minPriceAsActivityRaw(row);
  if (mp != null) return mp;
  const joined = lastResortJoinedRowActivityRaw(row);
  if (joined != null) return joined;
  const deep = deepPickActivityPriceScalar(row);
  if (deep != null) return deep;
  return scavengeActivitySaleRaw(row);
}

/**
 * 后端有时同时返回「档位日常价 price」与「档案原价 old_price」，二者略差于会员/合同价，
 * 易被误判成 recover（秒杀价在 price）。真 recover 应是活动价远低于日常价，或小额档位的大比例差。
 */
function looksLikeSeckillRecoverSale(catalog, oldP) {
  if (Number.isNaN(catalog) || Number.isNaN(oldP)) return false;
  if (oldP <= catalog + 0.001) return false;
  if (catalog <= 500) return true;
  return oldP / catalog >= 1.25;
}

/**
 * 接口常在嵌套对象里给 `price`，语义仍是「日常售卖价」；若与当前档 list 价相同则不能当活动价。
 */
function isPlausibleActivitySaleVsCatalog(raw, row) {
  if (raw == null || raw === '') return false;
  const coerced = coerceScalarPrice(raw);
  if (coerced == null || coerced === '') return false;
  const n = parseMoney(coerced);
  if (Number.isNaN(n)) return false;
  const cat = parseSkuRowListPriceRaw(row);
  if (Number.isNaN(cat)) return true;
  return n < cat - 0.01;
}

/**
 * 同档「日常售卖价」（划线价计算用）。
 *
 * 后端 Seckill::recoverSkuPrices：命中活动后 `price`=秒杀价、`old_price`=recover 前原价；
 * `item_goods_sku_price` 为活动规格记录，其 `price` 常与行上秒杀价一致，不能当作「日常价」。
 */
function parseSkuRowListPriceRaw(row) {
  if (!row || typeof row !== 'object') return NaN;

  const sale = parseMoney(row.price);
  const oldP = parseMoney(row.old_price);

  if (!Number.isNaN(oldP) && oldP > 0.01) {
    if (Number.isNaN(sale) || oldP > sale + 0.02) {
      return oldP;
    }
  }

  const item = row.item_goods_sku_price;
  if (item && typeof item === 'object') {
    const igDaily =
      item.original_goods_price ??
      item.original_price ??
      item.goods_price ??
      item.list_price ??
      item.market_price ??
      item.catalog_price;
    const igd = parseMoney(coerceScalarPrice(igDaily));
    if (!Number.isNaN(igd) && igd > 0.01) return igd;

    if (item.price != null && String(item.price).trim() !== '') {
      const ip = parseMoney(item.price);
      if (!Number.isNaN(ip) && !Number.isNaN(sale) && ip > sale + 0.02) return ip;
    }
  }

  if (row.goods_sku_price && typeof row.goods_sku_price === 'object' && row.goods_sku_price.price != null) {
    const gp = parseMoney(row.goods_sku_price.price);
    if (!Number.isNaN(gp) && gp > 0.01) {
      if (Number.isNaN(sale) || gp > sale + 0.02) return gp;
    }
  }

  return parseMoney(row.price);
}

function rowLooksJoinedActivity(row) {
  if (!row || typeof row !== 'object') return false;
  const ap0 = coerceScalarPrice(row.activity_price);
  if (ap0 != null && ap0 !== '' && !Number.isNaN(parseMoney(ap0))) return true;
  const sk0 = coerceScalarPrice(row.seckill_price);
  if (sk0 != null && sk0 !== '' && !Number.isNaN(parseMoney(sk0))) return true;
  if (row.is_join_activity === 1 || row.is_join_activity === '1' || row.is_join_activity === true) {
    return true;
  }
  if (row.join_activity === 1 || row.join_activity === '1' || row.join_activity === true) {
    return true;
  }
  const st = parseMoney(row.activity_stock);
  if (!Number.isNaN(st) && st > 0) return true;
  if (row.goods_activity_status === 'join' || row.activity_status === 'join') return true;
  return false;
}

/** 已参与活动的 SKU 行：直接读活动价（接口常只在「已参与」行上给 activity_price） */
function lastResortJoinedRowActivityRaw(row) {
  if (!rowLooksJoinedActivity(row)) return null;
  const keys = [
    'activity_price',
    'seckill_price',
    'activity_goods_price',
    'activity_sale_price',
    'event_price',
    'promotion_price',
  ];
  for (let i = 0; i < keys.length; i++) {
    const raw = coerceScalarPrice(row[keys[i]]);
    if (raw == null || raw === '') continue;
    const n = parseMoney(raw);
    if (Number.isNaN(n) || n < 0.01) continue;
    const cat = parseSkuRowListPriceRaw(row);
    if (!Number.isNaN(cat) && n >= cat - 0.01) continue;
    return raw;
  }
  if (row.min_price != null && String(row.min_price).trim() !== '') {
    const mn = parseMoney(row.min_price);
    const cat = parseSkuRowListPriceRaw(row);
    if (minPriceLooksLikeActivitySale(mn, cat)) return row.min_price;
  }
  return null;
}

function lastResortActivityPricesFromJoinedRows(goods) {
  const rows = effectiveSkuRowsForPricing(goods);
  if (!Array.isArray(rows) || !rows.length) return null;
  const out = [];
  for (let i = 0; i < rows.length; i++) {
    const raw = lastResortJoinedRowActivityRaw(rows[i]);
    if (raw == null) continue;
    const n = parseMoney(raw);
    if (!Number.isNaN(n)) out.push(String(n));
  }
  return out.length ? out : null;
}

/**
 * 解析单行秒杀「秒杀价 + 同档日常价（划线）」。
 * @returns {{ sale: number, listPrice: number }} 缺失则为 NaN
 */
export function parseSeckillSkuPair(row) {
  if (!row || typeof row !== 'object') return { sale: NaN, listPrice: NaN };

  let explicitRaw = parseExplicitActivityRaw(row);
  if (explicitRaw != null && explicitRaw !== '' && !isPlausibleActivitySaleVsCatalog(explicitRaw, row)) {
    explicitRaw = null;
  }
  const oldP = parseMoney(row.old_price);
  const catalog = parseMoney(row.price);
  const listFromItem = parseSkuRowListPriceRaw(row);

  const isRecoverShape =
    (explicitRaw == null || explicitRaw === '') &&
    row.old_price != null &&
    String(row.old_price).trim() !== '' &&
    row.price != null &&
    String(row.price).trim() !== '' &&
    looksLikeSeckillRecoverSale(catalog, oldP);

  // recover：秒杀价在 price，折扣前在 old_price（仅当价差符合秒杀，避免 2325 vs 2638 误判）
  if (isRecoverShape) {
    return {
      sale: catalog,
      listPrice: Number.isNaN(oldP) ? NaN : oldP,
    };
  }

  // 分列：activity_* + 同档日常价（item_goods_sku_price / price），不要用档案 old_price 盖住「价格」列
  if (explicitRaw != null && explicitRaw !== '') {
    const sale = parseMoney(explicitRaw);
    let listPrice = NaN;
    if (!Number.isNaN(listFromItem) && !Number.isNaN(sale) && listFromItem > sale + 0.001) {
      listPrice = listFromItem;
    } else if (!Number.isNaN(oldP) && !Number.isNaN(sale) && oldP > sale + 0.001) {
      listPrice = oldP;
    }
    return { sale, listPrice };
  }

  // 已参与秒杀：recover 后 row.price 即后台「活动价格」，即使与日常价相同也需展示
  if (row.item_goods_sku_price && typeof row.item_goods_sku_price === 'object') {
    const sale = parseMoney(row.price);
    if (!Number.isNaN(sale) && sale > 0) {
      let listPrice = NaN;
      if (!Number.isNaN(oldP) && oldP > sale + 0.001) {
        listPrice = oldP;
      } else if (!Number.isNaN(listFromItem) && listFromItem > sale + 0.001) {
        listPrice = listFromItem;
      }
      return { sale, listPrice };
    }
  }

  return { sale: NaN, listPrice: NaN };
}

/** 按规格项 id 匹配 sku_prices 行（勿用 children 下标，顺序可能与后台不一致） */
export function findSkuPriceRowBySpecItemId(rows, specItemId) {
  const sid = String(specItemId);
  if (!Array.isArray(rows) || !sid) return null;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row || typeof row !== 'object') continue;
    const raw = row.goods_sku_ids;
    const arr = Array.isArray(raw)
      ? raw.map((x) => String(x))
      : String(raw ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
    if (arr.indexOf(sid) >= 0) return row;
  }
  return null;
}

/** 秒杀多规格默认选中：第一个能解析出秒杀价的规格项 */
export function resolveSeckillSpecSelection(skus, skuPrices) {
  const spec = Array.isArray(skus) && skus.length ? skus[0] : null;
  const children = spec && spec.children ? spec.children : [];
  const prices = Array.isArray(skuPrices) ? skuPrices : [];
  if (!children.length || !prices.length) return null;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    const sp = findSkuPriceRowBySpecItemId(prices, child.id);
    if (!sp) continue;
    const { sale } = parseSeckillSkuPair(sp);
    if (!Number.isNaN(sale)) {
      return { specId: spec.id, specItemId: child.id, skuPrice: sp };
    }
  }

  const fallbackChild = children[0];
  const sp =
    findSkuPriceRowBySpecItemId(prices, fallbackChild.id) || prices[0];
  return { specId: spec.id, specItemId: fallbackChild.id, skuPrice: sp };
}

/**
 * 秒杀 recover 成功后：命中规格常有 min_price，或 old_price 与 price 并存。
 * 严禁用 original_price≠price 判断「全是活动价」（会拼出错误长串）。
 */
function collectSeckillRowHintPrices(rows) {
  if (!Array.isArray(rows) || !rows.length) return null;
  const vals = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row || typeof row !== 'object') continue;
    if (row.min_price != null && String(row.min_price).trim() !== '') {
      const mn = parseMoney(row.min_price);
      const cat = parseSkuRowListPriceRaw(row);
      if (minPriceLooksLikeActivitySale(mn, cat)) {
        vals.push(row.min_price);
        continue;
      }
    }
    const oldP = row.old_price != null && row.old_price !== '' ? row.old_price : null;
    const p = row.price;
    if (oldP != null && p != null && String(p) !== String(oldP)) {
      const pv = parseMoney(p);
      const ov = parseMoney(oldP);
      if (!looksLikeSeckillRecoverSale(pv, ov)) continue;
      vals.push(p);
    }
  }
  return vals.length ? vals : null;
}

function collapseSeckillPriceBand(values) {
  if (!Array.isArray(values) || values.length < 3) return values;
  const nums = values
    .map((x) => parseFloat(String(x).replace(/,/g, '')))
    .filter((n) => !Number.isNaN(n));
  if (nums.length < 3) return values;
  const mn = Math.min(...nums);
  const mx = Math.max(...nums);
  const fmt = (n) => {
    const s = n.toFixed(2);
    return s.endsWith('.00') ? s.slice(0, -3) : s;
  };
  return mn === mx ? [fmt(mn)] : [fmt(mn), fmt(mx)];
}

function fallbackDeepScanActivityPrices(goods) {
  const rows = effectiveSkuRowsForPricing(goods);
  const list = rows.length ? rows : goods.activity_sku_prices;
  if (!Array.isArray(list) || !list.length) return null;
  const vals = [];
  for (let i = 0; i < list.length; i++) {
    const row = list[i];
    if (!row || typeof row !== 'object') continue;
    let raw = deepPickActivityPriceScalar(row);
    if (raw == null) {
      raw = parseExplicitActivityRaw(row);
    }
    if (raw == null || raw === '') continue;
    const n = parseMoney(raw);
    if (Number.isNaN(n) || n < 1) continue;
    const cat = parseSkuRowListPriceRaw(row);
    if (!Number.isNaN(cat) && n >= cat - 0.01) continue;
    vals.push(n);
  }
  return vals.length ? vals.map((v) => String(v)) : null;
}

/**
 * 与后台 Goods::getPriceAttr 一致：recover 后「已命中活动规格」的行会挂 item_goods_sku_price，
 * 该行 price 即为秒杀价（与聚合 goods.price 区间的来源相同）。
 * parseSeckillSkuPair 在少数字段组合下可能解析不出 sale，此处按后端语义直接兜底。
 */
function collectActivityPricesFromActivityMarkedRows(rows) {
  if (!Array.isArray(rows) || !rows.length) return null;
  const vals = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row || typeof row !== 'object') continue;
    if (!row.item_goods_sku_price || typeof row.item_goods_sku_price !== 'object') continue;
    const p = parseMoney(row.price);
    if (!Number.isNaN(p) && p > 0.01) vals.push(p);
  }
  return vals.length ? vals.map((v) => String(v)) : null;
}

function collectActivityPricesFromSkuRows(goods) {
  let rows = effectiveSkuRowsForPricing(goods);
  if (!rows.length && Array.isArray(goods.activity_sku_prices) && goods.activity_sku_prices.length) {
    rows = goods.activity_sku_prices;
  }
  if (!Array.isArray(rows) || !rows.length) return null;

  const vals = [];
  for (let i = 0; i < rows.length; i++) {
    const { sale } = parseSeckillSkuPair(rows[i]);
    if (!Number.isNaN(sale)) vals.push(sale);
  }
  if (vals.length) return collapseSeckillPriceBand(vals.map((v) => String(v)));

  const markedBand = collectActivityPricesFromActivityMarkedRows(rows);
  if (markedBand && markedBand.length) return collapseSeckillPriceBand(markedBand);

  if (goods.activity_type === 'seckill') {
    const hinted = collectSeckillRowHintPrices(rows);
    if (hinted) return collapseSeckillPriceBand(hinted);
    const deep = fallbackDeepScanActivityPrices(goods);
    return deep ? collapseSeckillPriceBand(deep) : null;
  }
  return null;
}

/**
 * 后端 getPriceAttr：命中活动时 goods.price 常为「活动价区间」；未命中时与 SKU 日常最低价一致（如仅 1915）。
 * 若 goods.price 最小值 < 各档 SKU 日常价最小值，则视为活动价区间，可与 SKU 行同时展示。
 */
function isGoodsLevelPriceActivityBand(goods) {
  if (!goods || goods.activity_type !== 'seckill') return false;
  const p = goods.price;
  if (p == null || p === '') return false;
  const parr = Array.isArray(p) ? p : [p];
  const bandNums = parr
    .map((x) => parseMoney(x))
    .filter((n) => !Number.isNaN(n) && n > 0);
  if (!bandNums.length) return false;
  const bandMin = Math.min(...bandNums);
  const rows = goods.new_sku_prices || goods.sku_prices;
  if (!Array.isArray(rows) || !rows.length) return true;
  let catMin = Infinity;
  for (let i = 0; i < rows.length; i++) {
    const c = parseSkuRowListPriceRaw(rows[i]);
    if (!Number.isNaN(c) && c > 0) catMin = Math.min(catMin, c);
  }
  if (!Number.isFinite(catMin)) return true;
  return bandMin < catMin - 0.5;
}

export function getGoodsActivityPriceArray(goods) {
  if (!goods || typeof goods !== 'object') return null;

  const fromRows = collectActivityPricesFromSkuRows(goods);
  if (fromRows && fromRows.length) return fromRows;

  const candidates = [
    goods.activity_price,
    goods.activity_goods_price,
    goods.activity_sale_price,
    goods.seckill_price,
    goods.event_price,
    goods.promotion_price,
    goods.sale_price,
    goods.sku_activity_prices,
    goods.spec_activity_price,
  ];
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    if (c == null || c === '') continue;
    if (Array.isArray(c)) {
      const filtered = c.filter((x) => x != null && x !== '');
      if (filtered.length) {
        return goods.activity_type === 'seckill'
          ? collapseSeckillPriceBand(filtered)
          : filtered;
      }
      continue;
    }
    return [c];
  }
  /** 部分接口把区间挂在嵌套 activity 上 */
  let minA = goods.min_activity_price;
  let maxA = goods.max_activity_price;
  const actNest = goods.activity;
  if (
    (minA == null || minA === '') &&
    actNest &&
    typeof actNest === 'object'
  ) {
    minA =
      actNest.min_activity_price ??
      actNest.min_price ??
      actNest.activity_price ??
      actNest.activity_goods_price ??
      actNest.seckill_price ??
      actNest.flash_price;
    if (maxA == null || maxA === '') {
      maxA = actNest.max_activity_price ?? actNest.max_price;
    }
  }
  if (minA != null && minA !== '') {
    const mn = parseMoney(minA);
    const skuRows = goods.new_sku_prices || goods.sku_prices;
    let catFloor = Infinity;
    if (Array.isArray(skuRows) && skuRows.length) {
      for (let r = 0; r < skuRows.length; r++) {
        const c = parseSkuRowListPriceRaw(skuRows[r]);
        if (!Number.isNaN(c) && c > 0) catFloor = Math.min(catFloor, c);
      }
    }
    if (
      !Number.isNaN(mn) &&
      Number.isFinite(catFloor) &&
      mn >= catFloor - 0.01
    ) {
      // 与日常最低价相同：多为未命中活动时的占位，不能当活动价
    } else {
      if (maxA != null && maxA !== '' && String(maxA) !== String(minA)) {
        return [minA, maxA];
      }
      return [minA];
    }
  }
  if (goods.activity_type === 'seckill') {
    const joinedBand = lastResortActivityPricesFromJoinedRows(goods);
    if (joinedBand && joinedBand.length) {
      return collapseSeckillPriceBand(joinedBand);
    }
    const deep = fallbackDeepScanActivityPrices(goods);
    if (deep && deep.length) {
      return collapseSeckillPriceBand(deep);
    }
  }
  const p = goods.price;
  if (p == null || p === '') return null;
  const skuRows = goods.new_sku_prices || goods.sku_prices;
  if (
    goods.activity_type === 'seckill' &&
    Array.isArray(skuRows) &&
    skuRows.length > 0
  ) {
    if (isGoodsLevelPriceActivityBand(goods)) {
      const arr = Array.isArray(p) ? p : [p];
      return collapseSeckillPriceBand(arr);
    }
    return null;
  }
  const arr = Array.isArray(p) ? p : [p];
  return goods.activity_type === 'seckill' ? collapseSeckillPriceBand(arr) : arr;
}

function minSeckillSaleFromGoods(goods) {
  const arr = getGoodsActivityPriceArray(goods);
  if (!arr || !arr.length) return null;
  const nums = arr.map((x) => parseFloat(String(x).replace(/,/g, ''))).filter((n) => !Number.isNaN(n));
  if (!nums.length) return null;
  return Math.min(...nums);
}

/**
 * 接口未返回活动价时：用各档「价格」列日常价的最低值展示（常与商品层 price 区间最小值一致，如 1915）。
 * 后台补上 activity_price 后将自动走真实秒杀价逻辑。
 */
export function formatSeckillCardPriceCatalogFallback(goods) {
  if (!goods || goods.activity_type !== 'seckill') return '';
  const rows = goods.new_sku_prices || goods.sku_prices;
  if (Array.isArray(rows) && rows.length) {
    const nums = [];
    for (let i = 0; i < rows.length; i++) {
      const v = parseSkuRowListPriceRaw(rows[i]);
      if (!Number.isNaN(v) && v > 0) nums.push(v);
    }
    if (nums.length) return formatMoneyText(Math.min(...nums));
  }
  const p = goods.price;
  if (p == null || p === '') return '';
  const parr = Array.isArray(p) ? p : [p];
  const parsed = parr.map((x) => parseMoney(x)).filter((n) => !Number.isNaN(n) && n > 0);
  if (parsed.length) return formatMoneyText(Math.min(...parsed));
  return '';
}

/** 仅秒杀活动价数字（不含日常价兜底），用于与「日常价」分行展示 */
export function formatSeckillActivityPriceOnlyDisplay(goods) {
  const arr = getGoodsActivityPriceArray(goods);
  if (!arr || !arr.length) return '';
  const nums = arr.map((x) => parseFloat(String(x).replace(/,/g, ''))).filter((n) => !Number.isNaN(n));
  if (!nums.length) return arr.join('~');
  const mn = Math.min(...nums);
  const s = mn.toFixed(2);
  return s.endsWith('.00') ? s.slice(0, -3) : s;
}

/**
 * 秒杀卡片主标价（兼容旧用法）：有活动价则取最低活动价；否则用日常价兜底（单行场景）。
 */
export function formatSeckillPrimaryPriceDisplay(goods) {
  const act = formatSeckillActivityPriceOnlyDisplay(goods);
  if (act) return act;
  return formatSeckillCardPriceCatalogFallback(goods) || '';
}

/** 卡片「日常价」行：各档日常价最低值，与秒杀价同时展示 */
export function formatSeckillCatalogFloorLineDisplay(goods) {
  return formatSeckillCardPriceCatalogFallback(goods) || '';
}

/**
 * 秒杀卡片划线价：与「最低秒杀价」同一档对应的日常价（后台「价格」列 / old_price）。
 */
export function getSeckillRefPriceText(goods) {
  if (!goods || goods.activity_type !== 'seckill') return '';

  const saleMin = minSeckillSaleFromGoods(goods);
  if (saleMin == null || Number.isNaN(saleMin)) return '';

  const effRows = effectiveSkuRowsForPricing(goods);
  const rows =
    effRows.length > 0
      ? effRows
      : goods.new_sku_prices || goods.sku_prices || goods.activity_sku_prices || [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const { sale, listPrice } = parseSeckillSkuPair(row);
    if (Number.isNaN(sale)) continue;
    if (Math.abs(sale - saleMin) > 0.02) continue;
    let ref = listPrice;
    if (Number.isNaN(ref) || ref <= saleMin + 0.001) {
      const cat = parseSkuRowListPriceRaw(row);
      if (!Number.isNaN(cat) && cat > saleMin + 0.001) ref = cat;
    }
    if (!Number.isNaN(ref) && ref > saleMin + 0.001) return formatMoneyText(ref);
  }

  const paired = [];
  for (let j = 0; j < rows.length; j++) {
    const row = rows[j];
    const { sale, listPrice } = parseSeckillSkuPair(row);
    if (Number.isNaN(sale)) continue;
    let ref = listPrice;
    if (Number.isNaN(ref) || ref <= saleMin + 0.001) {
      const cat = parseSkuRowListPriceRaw(row);
      if (!Number.isNaN(cat) && cat > saleMin + 0.001) ref = cat;
    }
    if (Number.isNaN(ref) || Number.isNaN(sale)) continue;
    if (ref > saleMin + 0.001) paired.push(ref);
  }
  if (paired.length) return formatMoneyText(Math.max(...paired));

  if (goods.original_price != null && String(goods.original_price).trim() !== '') {
    const ref = parseMoney(goods.original_price);
    if (!Number.isNaN(ref) && ref > saleMin + 0.001) return formatMoneyText(ref);
  }

  const origs = [];
  for (let k = 0; k < rows.length; k++) {
    const row = rows[k];
    if (!row || typeof row !== 'object') continue;
    if (row.original_price != null && String(row.original_price).trim() !== '') {
      const v = parseMoney(row.original_price);
      if (!Number.isNaN(v)) origs.push(v);
    }
  }
  if (origs.length) {
    const ref = Math.max(...origs);
    if (ref > saleMin + 0.001) return formatMoneyText(ref);
  }
  return '';
}

const ACTIVITY_SKU_MERGE_KEYS = [
  'activity_price',
  'activityPrice',
  'activity_goods_price',
  'activity_sale_price',
  'seckill_price',
  'seckillPrice',
  'event_price',
  'promotion_price',
];

/**
 * 详情/活动接口常把 activity_sku_prices 与 sku_prices 分开展示：合并到 sku 行上便于统一解析。
 */
/**
 * 详情接口常只给 new_sku_prices，前端多处只读 sku_prices → 规格与秒杀价整块失效。
 */
export function normalizeGoodsSkuPriceArrays(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const out = { ...payload };
  const n = out.new_sku_prices;
  const s = out.sku_prices;
  if (Array.isArray(n) && n.length > 0 && (!Array.isArray(s) || s.length === 0)) {
    out.sku_prices = n;
  }
  if (Array.isArray(s) && s.length > 0 && (!Array.isArray(n) || n.length === 0)) {
    out.new_sku_prices = s;
  }
  return out;
}

export function mergeActivitySkuIntoSkuPrices(payload) {
  if (!payload || payload.activity_type !== 'seckill') return payload;
  const extra = pickActivitySkuPricesExtra(payload);
  if (!Array.isArray(extra) || !extra.length) return payload;

  const idMap = new Map();
  for (let e = 0; e < extra.length; e++) {
    const row = extra[e];
    if (!row || typeof row !== 'object') continue;
    const sid = row.goods_sku_price_id ?? row.sku_price_id ?? row.id;
    if (sid != null) idMap.set(String(sid), row);
  }

  const mergeOne = (main) => {
    if (!Array.isArray(main) || !main.length) return main;
    return main.map((row, i) => {
      if (!row || typeof row !== 'object') return row;
      let act = extra[i] && typeof extra[i] === 'object' ? extra[i] : null;
      if (!act && row.id != null) act = idMap.get(String(row.id)) || null;
      if (!act) return row;
      const o = { ...row };
      for (let k = 0; k < ACTIVITY_SKU_MERGE_KEYS.length; k++) {
        const key = ACTIVITY_SKU_MERGE_KEYS[k];
        const v = act[key];
        if (v != null && v !== '' && (o[key] == null || o[key] === '')) o[key] = v;
      }
      for (const [key, v] of Object.entries(act)) {
        if (v == null || v === '' || typeof v === 'object') continue;
        if (!/(activity|seckill|flash|promotion|event|rush|特价).*price|^(event|promotion)_price$/i.test(key)) {
          continue;
        }
        if (/id|status|type|stock|time$/i.test(key)) continue;
        if (o[key] == null || o[key] === '') o[key] = v;
      }
      if (act.min_price != null && String(act.min_price).trim() !== '') {
        const mn = parseMoney(act.min_price);
        const cat = parseSkuRowListPriceRaw(o);
        if (
          minPriceLooksLikeActivitySale(mn, cat) &&
          (o.min_price == null || String(o.min_price).trim() === '')
        ) {
          o.min_price = act.min_price;
        }
      }
      return o;
    });
  };

  const out = { ...payload };
  const sku = payload.sku_prices;
  const nsku = payload.new_sku_prices;
  if (Array.isArray(nsku) && Array.isArray(sku) && nsku === sku) {
    const m = mergeOne(sku);
    out.sku_prices = m;
    out.new_sku_prices = m;
  } else {
    if (Array.isArray(sku)) out.sku_prices = mergeOne(sku);
    if (Array.isArray(nsku)) out.new_sku_prices = mergeOne(nsku);
  }
  return out;
}

/** 同档「价格」列（日常价），供详情在解析出秒杀价后补划线 */
export function getSeckillSkuCatalogPrice(row) {
  const v = parseSkuRowListPriceRaw(row);
  return Number.isNaN(v) ? NaN : v;
}
